import { useRollbar } from '@rollbar/react';
import axios from 'axios';

import { SUNRUN_URL } from '@/constants/baseUrls';
import { trackEvents } from '@/utils/analytics';
import {
  AllowedAttributionParams,
  getAttributionParams,
  getCampaignID,
} from '@/utils/attribution/attribution';
import { getCurrentLeadIdToken, refreshLeadIdToken } from '@/utils/leadIdToken';
import { trimPhone } from '@/utils/phone';
import { getXxTrustedFormCertUrl } from '@/utils/tcpaValidation';

import useEligibility, { EligibilityType } from './useEligibility';

export enum OwnHome {
  OWN = 'OWN',
  RENT = 'RENT',
}

export interface LeadFormSubmitData extends AllowedAttributionParams {
  // Fields required for tcpa validation
  universal_leadid: string;
  xxTrustedFormCertUrl: string;

  // Standard lead form properties
  first_name?: string;
  last_name?: string;
  zip?: string;
  email?: string;
  phone_home?: string;
  property_ownership?: OwnHome | undefined;

  // Embedded Lead form properties
  electric_bill?: string;
  additionalServices?: string;
  Referral_web_form__c?: string;
}

export type LeadFormInputData = Omit<
  LeadFormSubmitData,
  'universal_leadid' | 'xxTrustedFormCertUrl'
>;

const sanitizeZeusData = (zeusData: LeadFormInputData): LeadFormInputData => {
  const sanitized = {
    ...zeusData,
    phone_home: trimPhone(zeusData.phone_home),
    first_name: zeusData.first_name?.trim(),
    last_name: zeusData.last_name?.trim(),
  };
  return sanitized;
};

export function useSubmitLeadToZeus() {
  const { fetchEligibility } = useEligibility();
  const rollbar = useRollbar();

  async function submitLeadToZeus({
    zeusData,
    contentfulCampaignId,
    isEcomm = false,
    gtmSubmitEventFormType = 'Standard Lead Form',
    overrideCampaignId = false,
    honeypot = false,
  }: {
    zeusData: LeadFormInputData;
    contentfulCampaignId?: string;
    gtmSubmitEventFormType?: string;
    overrideCampaignId?: boolean;
    isEcomm?: boolean;
    honeypot?: boolean;
  }) {
    const attributionParams = getAttributionParams();
    const campid = getCampaignID({
      overrideCampaignId,
      isEcomm,
      contentfulCampaignId,
    });
    const sanitizedZeusData = sanitizeZeusData(zeusData);

    // Retrieve lead validation tokens
    const universal_leadid = getCurrentLeadIdToken();
    const xxTrustedFormCertUrl = getXxTrustedFormCertUrl();

    // Adds attribution parameters and campid to zeus data
    const enhancedZeusData: LeadFormSubmitData = {
      ...sanitizedZeusData,
      universal_leadid,
      xxTrustedFormCertUrl,
      ...attributionParams,
      campid,
    };

    const inService = sanitizedZeusData.zip
      ? fetchEligibility(EligibilityType.WEBSITE, sanitizedZeusData.zip)
      : false;

    trackEvents({
      event: 'Form Submitted',
      form_type: gtmSubmitEventFormType,
      ...(sanitizedZeusData.email && {
        form_submit_email: sanitizedZeusData.email,
      }),
      ...(sanitizedZeusData.property_ownership && {
        home_ownership: sanitizedZeusData.property_ownership,
      }),
      referral_status: sanitizedZeusData.Referral_web_form__c
        ? 'Referred'
        : 'not referred',
      in_service: inService,
      honeypot,
    });

    if (!enhancedZeusData.universal_leadid) {
      rollbar.error('Error: No leadId token was found during lead submission');
    }

    const zeusRes = await axios.post(`${SUNRUN_URL}/v1/lead/submit`, null, {
      params: enhancedZeusData,
    });

    rollbar.info('Zeus Lead Submitted', {
      hasLeadId: Boolean(enhancedZeusData.universal_leadid),
      initiator: gtmSubmitEventFormType,
      honeypot,
    });

    // After a form submit refresh the top level leadId token in case there are multiple forms on the page
    refreshLeadIdToken();

    return zeusRes.data;
  }

  return { submitLeadToZeus };
}
